import { EventFactory, useAPI } from '#/hooks/index.js';
import { useCallback, useEffect, useRef } from 'react';
import scopeLogger from '#/lib/scope-logger.js';
import { filter } from 'rxjs';
import { tokenContent } from '#/lib/token-content.js';

const systemBackMessages = filter(
  (message) => message.event_type === 'system' && message.event_code === 'back',
);

const console = scopeLogger('RMI:useForeground');

const html = document.documentElement;
const viewportMeta = document.querySelector('meta[name="viewport"]');
const fullScreenViewportMeta = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=no, viewport-fit=cover';
const standardViewportMeta = 'width=device-width, initial-scale=1.0, maximum-scale=1.0, user-scalable=yes, viewport-fit=auto';
let hasViewportFit = false;

/**
 * Listen on passed container for entering slide event and request fullscreen mode on demand.
 * @todo Does it make sense to level-up such listener up to root element, e.g. no React at all?
 * @todo Extract business logic into own module independent from React.
 * @param containerRef
 * @param currentObject
 */
export function useForeground(containerRef, currentObject) {
  const API = useAPI();

  useEffect(() => {
    const subscription = API.configProvider.config.subscribe((config) => {
      const token = tokenContent(config.token);
      hasViewportFit = /iphone/i.test(token.public_key);
    });

    return () => {
      subscription.unsubscribe();
    }
  }, [API.configProvider.config]);

  const slidesStack = useRef([]);

  console.log('useForeground', containerRef, currentObject);

  const requestForeground = useCallback((options) => {
    if (hasViewportFit) {
      viewportMeta.setAttribute('content', fullScreenViewportMeta);
      html.classList.add('fullscreen');
    }
    API.hostApp.requestForeground(options);

    console.log('send request FG');

  }, [API.hostApp, hasViewportFit]);

  const releaseForeground = useCallback((options) => {
    if (hasViewportFit) {
      viewportMeta.setAttribute('content', standardViewportMeta);
      html.classList.remove('fullscreen');
    }
    API.hostApp.releaseForeground(options);
    console.log('send release FG');
  }, [API.hostApp]);

  useEffect(() => {
    const subscription = API.hostApp.message$
      .pipe(systemBackMessages,)
      .subscribe(() => {
        if (slidesStack.current.length === 0) {
          return;
        }

        const fakeClickEvent = new EventFactory({
          link: 'megogo://close',
          id: 'system-back',
          type: 'virtual button'
        });

        /*
        Suggest: we have the only one creative at same time.
        So, dispatch fake close click to any slide should lead to closing creative at all.
        */
        slidesStack.current[0].DOMElement.dispatchEvent(fakeClickEvent);
      })

    return () => {
      subscription.unsubscribe();
    };
  }, [API.hostApp.message$]);

  useEffect(() => {
    /* We should release foreground on unloading. */
    window.addEventListener('beforeunload', () => {
      API.hostApp.releaseForeground();
    });
  }, [API.hostApp]);

  useEffect(() => {
    if (containerRef.current === null) {
      console.log('containerRef.current === null')
      return;
    }
    const target = containerRef.current;

    const applyPlayerShape = () => {
      const slide = slidesStack.current[0];

      if (!slide) {
        return;
      }

      if (slide._displayZone !== 'mini-player') {
        return;
      }

      if (false === Object.hasOwn(slide, '_playerShape')) {
        console.warn('Player shape getter is not defined yet');
        return;
      }

      if (target.getAttribute('data-hidden') === "true") {
        console.warn('Looks like app is hidden');
        return
      }

      slide._playerShape.then(
        (shape) => {
          /* the following parameters necessary for apply shape on scaled element/container */
          const shapeScale = slide.scale ?? 1;

          requestForeground({
            mode: slide._displayZone,
            parameters: {
              width: shape.width / shapeScale,
              height: shape.height / shapeScale,
              top: shape.top / shapeScale,
              left: shape.left / shapeScale,
              duration: `${slide.transition?.duration ?? 500}ms`,
            },
          });
        },
        (error) => {
          const customError = new CustomEvent('error', {
            bubbles: true,
            cancelable: true,
            detail: {
              error,
            },
          });
          slide.DOMElement.dispatchEvent(customError);
        },
      );
    };

    const onSlideEnter = (event) => {
      const slide = event.detail.origin.target;
      const isFullscreen = slide._displayMode === 'fullscreen';

      if (!isFullscreen) {
        return;
      }

      console.log('enter fullscreen slide');

      slide.DOMElement = event.target;
      slidesStack.current.unshift(slide);

      if (slide._displayZone === 'mini-player') {
        applyPlayerShape();
        const veryOwnListener = (_event) => {
          applyPlayerShape();
        };
        window.addEventListener('resize', veryOwnListener);
        slide.dispose = () => {
          window.removeEventListener('resize', veryOwnListener);
        };
        slide.DOMElement.addEventListener('slide-hide', slide.dispose);
      } else {
        requestForeground({
          mode: slide._displayZone,
          parameters: {
            isTriggerPlayerPause: slide.isTriggerPlayerPause,
          },
        });
      }
    };

    const onSlideHidden = (event) => {
      if (event.detail.origin.target._displayMode !== 'fullscreen') {
        return;
      }

      const slide = slidesStack.current.pop();

      slide?.dispose?.();

      if (slidesStack.current.length === 0) {
        // instruct to release foreground exactly when last slide is hidden
        releaseForeground({
          isTriggerPlayerPause: slide?.isTriggerPlayerPause,
        });

        return;
      }

      /*
        in case removed slide had has "pause" flag, but current slide doesn't have it,
        we should not actually release foreground, but un-pause player
      */
      if (
        slide.isTriggerPlayerPause &&
        slidesStack.current[0].isTriggerPlayerPause !==
          slide.isTriggerPlayerPause
      ) {
        API.hostApp.removeControl({
          parameters: { isTriggerPlayerPause: slide.isTriggerPlayerPause },
        });
      }
    };

    console.log('setup listeners');
    target.addEventListener('slide-enter', onSlideEnter);
    target.addEventListener('slide-hide', onSlideHidden);

    return () => {
      console.log('remove listeners');
      target.removeEventListener('slide-enter', onSlideEnter);
      target.removeEventListener('slide-hide', onSlideHidden);

      slidesStack.current.forEach((slide) => {
        console.log('slide.dispose', slide);
        slide.dispose?.();
        releaseForeground({
          isTriggerPlayerPause: slide.isTriggerPlayerPause,
        });
      });

    };
  }, [API.hostApp, containerRef, currentObject, releaseForeground, requestForeground]);
}
